import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Badge, Button, Card, CardBody, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError, newIdempotencyKey } from '../lib/api.js';
import { getAllEmployees } from '../lib/employees.js';
import { useAuth } from '../auth/AuthContext.js';
import { useMasterPerRequestScope } from '../lib/masterScopedRequest.js';
import { EnrollmentFeedback, type EnrollmentPhase } from '../components/EnrollmentFeedback.js';
import { isValidPinCode, normalizePinCodeInput } from '../lib/pinCode.js';

type EmployeeRole = 'employee' | 'manager';

interface TenantSummary {
  id: string;
  name: string;
  status: 'active' | 'suspended' | 'archived';
}

interface EmployeeRow {
  id: string;
  name: string;
  email: string;
  role: EmployeeRole;
  status: 'invited' | 'active' | 'suspended' | 'offboarded';
  sync_status: 'synced' | 'pending' | 'failed';
  unifi_user_id: string | null;
  schedule_id?: string | null;
}

interface DeviceRow {
  id: string;
  name: string;
  kind: string;
  status: string;
  label_override?: string | null;
}

interface TouchPassRow {
  id: string;
  card_id: string | null;
  card_name: string | null;
  status: string;
  user_id: string | null;
}

interface EnrollmentSession {
  enrollment_id: string;
  expires_in_sec: number;
  status: 'waiting' | 'detected';
}

interface AccessZoneRow {
  id: string;
  name: string;
  schedule_id: string | null;
  sync_status: string;
  controller_enforced: boolean;
  assigned: boolean;
}

interface ScheduleSummary {
  id: string;
  name: string;
}

type CredentialKind = 'touch_pass' | 'nfc_fob' | 'pin_code';

function readerDisplayName(reader: DeviceRow): string {
  return reader.label_override || reader.name || reader.id;
}

export function CredentialEnrollmentPage() {
  const [params] = useSearchParams();
  const auth = useAuth();
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [tenantId, setTenantId] = useState(params.get('tenantId') ?? '');
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [employeeId, setEmployeeId] = useState(params.get('employeeId') ?? '');
  const [personMode, setPersonMode] = useState<'existing' | 'create'>(
    params.get('employeeId') ? 'existing' : 'create',
  );
  const [newName, setNewName] = useState(params.get('name') ?? '');
  const [newEmail, setNewEmail] = useState(params.get('email') ?? '');
  const [newRole, setNewRole] = useState<EmployeeRole>(params.get('role') === 'manager' ? 'manager' : 'employee');
  const [newScheduleId, setNewScheduleId] = useState('');
  const [schedules, setSchedules] = useState<ScheduleSummary[]>([]);
  const [tenantAccessZones, setTenantAccessZones] = useState<AccessZoneRow[]>([]);
  const [newAccessZoneId, setNewAccessZoneId] = useState('');
  const [readers, setReaders] = useState<DeviceRow[]>([]);
  const [readerId, setReaderId] = useState('');
  const [touchPasses, setTouchPasses] = useState<TouchPassRow[]>([]);
  const [touchPassId, setTouchPassId] = useState('');
  const [touchPassLoadError, setTouchPassLoadError] = useState<string | null>(null);
  // UX-034: EmployeeDetailPage links here to issue a specific credential type
  // for a specific person, so honour ?type= rather than making the operator
  // re-pick what they already chose.
  const [credentialKind, setCredentialKind] = useState<CredentialKind>(() => {
    const requested = params.get('type');
    return requested === 'pin_code' || requested === 'touch_pass' || requested === 'nfc_fob'
      ? requested
      : 'nfc_fob';
  });
  const [label, setLabel] = useState(credentialKind === 'pin_code' ? 'PIN' : credentialKind === 'touch_pass' ? 'Touch Pass' : 'Fob');
  const [pinCode, setPinCode] = useState('');
  const [enrollment, setEnrollment] = useState<EnrollmentSession | null>(null);
  // Drives the full-attention enrollment popup. `null` = popup closed.
  const [enrollPhase, setEnrollPhase] = useState<EnrollmentPhase | null>(null);
  const [enrollVariant, setEnrollVariant] = useState<'scan' | 'instant'>('scan');
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  // Held across the lifetime of one scan so a completion retry de-dupes.
  const completeKeyRef = useRef<string | null>(null);
  // UX-013: these were minted inside their submit handlers, so every retry was
  // a new intent to the backend. The create-person one matters most — a POST
  // that times out but succeeds server-side, retried with a fresh key, creates
  // a duplicate person, which is exactly the partial outcome the rest of this
  // flow is careful about. Each key is rotated when its intent completes, not
  // when it is attempted.
  const createPersonKeyRef = useRef<string>(newIdempotencyKey());
  const issueCredentialKeyRef = useRef<string>(newIdempotencyKey());
  // Guards the auto-issue so a fob is only handed out once per scan.
  const completingRef = useRef(false);
  const expiresAtRef = useRef<number | null>(null);
  const [zones, setZones] = useState<AccessZoneRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const tenantOperatorTenant = useMemo<TenantSummary | null>(() => {
    if (auth.state.status !== 'authed') return null;
    if (auth.state.user.role === 'master_admin' && auth.state.user.tenant_id == null) return null;
    const id = auth.state.user.tenant_id ?? auth.state.me.default_tenant_id;
    if (!id) return null;
    return {
      id,
      name: auth.state.me.tenant?.name ?? 'Current tenant',
      status: 'active',
    };
  }, [auth.state]);

  const canPickTenant =
    auth.state.status === 'authed' &&
    auth.state.user.role === 'master_admin' &&
    auth.state.user.tenant_id == null;
  const scopeOpts = useMasterPerRequestScope(tenantId);
  const touchPolicyEnabled =
    auth.state.status === 'authed' && auth.state.me.credential_policy.touch_credential_enabled;
  const thirdPartyNfcEnabled =
    auth.state.status === 'authed' && auth.state.me.credential_policy.third_party_nfc_enabled;
  const pinCodeEnabled =
    auth.state.status === 'authed' && auth.state.me.credential_policy.pin_code_enabled;
  const enrollmentReaderMode =
    auth.state.status === 'authed' ? auth.state.me.credential_policy.enrollment_reader_mode : 'any';
  const enrollmentReaderId =
    auth.state.status === 'authed' ? auth.state.me.credential_policy.enrollment_reader_id : null;
  const enrollmentReaderName =
    auth.state.status === 'authed' ? auth.state.me.credential_policy.enrollment_reader_name : null;
  const hasPinnedEnrollmentReader =
    enrollmentReaderMode === 'specified' && Boolean(enrollmentReaderId);

  const selectedEmployee = useMemo(
    () => employees.find((employee) => employee.id === employeeId) ?? null,
    [employeeId, employees],
  );

  const employeeReady = selectedEmployee?.status === 'active' &&
    selectedEmployee.sync_status === 'synced' && Boolean(selectedEmployee.unifi_user_id);

  function chooseCredentialKind(kind: CredentialKind) {
    setCredentialKind(kind);
    if (kind !== 'nfc_fob') {
      setEnrollment(null);
    }
    if (kind === 'touch_pass' && (label === 'Fob' || label === 'PIN')) setLabel('Touch Pass');
    if (kind === 'nfc_fob' && (label === 'Touch Pass' || label === 'PIN')) setLabel('Fob');
    if (kind === 'pin_code' && (label === 'Touch Pass' || label === 'Fob')) setLabel('PIN');
  }

  useEffect(() => {
    let cancelled = false;
    if (tenantOperatorTenant) {
      setTenants([tenantOperatorTenant]);
      if (!tenantId || tenantId !== tenantOperatorTenant.id) {
        setTenantId(tenantOperatorTenant.id);
      }
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    if (!canPickTenant) {
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    apiClient
      .get<{ data: TenantSummary[] }>('/api/v1/tenants', { skipTenantScope: true })
      .then((res) => {
        if (cancelled) return;
        setTenants(res.data.filter((tenant) => tenant.status === 'active'));
        if (!tenantId && res.data.length === 1) setTenantId(res.data[0]!.id);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load tenants.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canPickTenant, tenantId, tenantOperatorTenant]);

  useEffect(() => {
    if (!tenantId) {
      setEmployees([]);
      setSchedules([]);
      setTenantAccessZones([]);
      setEmployeeId('');
      setNewAccessZoneId('');
      setReaders([]);
      setReaderId('');
      return;
    }
    let cancelled = false;
    setError(null);
    const readerRequest = thirdPartyNfcEnabled
      ? apiClient
          .get<{ data: DeviceRow[] }>(
            '/api/v1/employees/credentials/enrollment-readers',
            scopeOpts,
          )
          .then((res) => res.data)
      : Promise.resolve([] as DeviceRow[]);

    Promise.all([
      getAllEmployees<EmployeeRow>(scopeOpts),
      readerRequest,
      apiClient
        .get<{ data: ScheduleSummary[] }>('/api/v1/schedules', scopeOpts)
        .then((res) => ({
          data: Array.isArray(res.data) ? res.data : ([] as ScheduleSummary[]),
        }))
        .catch(() => ({ data: [] as ScheduleSummary[] })),
      apiClient
        .get<{ data: { assigned: AccessZoneRow[]; available: AccessZoneRow[] } }>(
          '/api/v1/employees/access-zones',
          scopeOpts,
        )
        .then((res) => [...res.data.assigned, ...res.data.available])
        .catch(() => [] as AccessZoneRow[]),
    ])
      .then(([employeeRes, readerRows, scheduleRes, accessZoneRows]) => {
        if (cancelled) return;
        setEmployees(employeeRes.data);
        setSchedules(scheduleRes.data);
        setTenantAccessZones(accessZoneRows);
        setNewAccessZoneId('');
        const urlEmployeeId = params.get('employeeId');
        if (urlEmployeeId && employeeRes.data.some((employee) => employee.id === urlEmployeeId)) {
          setEmployeeId(urlEmployeeId);
          setPersonMode('existing');
        }
        setReaders(readerRows);
        setReaderId(
          hasPinnedEnrollmentReader ? (enrollmentReaderId ?? '') : readerRows[0]?.id || '',
        );
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load enrollment data.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    enrollmentReaderId,
    hasPinnedEnrollmentReader,
    params,
    scopeOpts,
    tenantId,
    thirdPartyNfcEnabled,
  ]);

  useEffect(() => {
    if (!tenantId || credentialKind !== 'touch_pass') {
      setTouchPasses([]);
      setTouchPassId('');
      setTouchPassLoadError(null);
      return;
    }
    let cancelled = false;
    setTouchPassLoadError(null);
    apiClient
      .get<{ data: TouchPassRow[] }>(
        '/api/v1/employees/credentials/touch-passes/assignable',
        scopeOpts,
      )
      .then((res) => {
        if (cancelled) return;
        setTouchPasses(res.data);
        setTouchPassId((current) => current || res.data[0]?.id || '');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setTouchPasses([]);
        setTouchPassId('');
        setTouchPassLoadError(
          err instanceof ApiError ? err.message : 'Could not load tenant Touch Passes.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [credentialKind, scopeOpts, tenantId]);

  useEffect(() => {
    if (credentialKind === 'touch_pass' && !touchPolicyEnabled) {
      chooseCredentialKind(thirdPartyNfcEnabled ? 'nfc_fob' : 'pin_code');
    } else if (credentialKind === 'nfc_fob' && !thirdPartyNfcEnabled) {
      chooseCredentialKind(touchPolicyEnabled ? 'touch_pass' : 'pin_code');
    }
  }, [credentialKind, thirdPartyNfcEnabled, touchPolicyEnabled]);

  useEffect(() => {
    if (!tenantId || !employeeId) {
      setZones([]);
      return;
    }
    let cancelled = false;
    apiClient
      .get<{ data: { assigned: AccessZoneRow[]; available: AccessZoneRow[] } }>(
        `/api/v1/employees/${employeeId}/access-zones`,
        scopeOpts,
      )
      .then((res) => {
        if (cancelled) return;
        setZones([...res.data.assigned, ...res.data.available]);
      })
      .catch(() => {
        if (!cancelled) setZones([]);
      });
    return () => {
      cancelled = true;
    };
  }, [employeeId, scopeOpts, tenantId]);

  const closeEnrollModal = useCallback(() => {
    setEnrollPhase(null);
    setEnrollError(null);
    setSecondsLeft(null);
    setEnrollment(null);
    expiresAtRef.current = null;
    completingRef.current = false;
  }, []);

  // Hand the detected fob to the controller. Auto-fires from the scan poll
  // the instant a card is detected, so the operator never has to click.
  const completeEnrollment = useCallback(async () => {
    if (!tenantId || !employeeId || !enrollment) return;
    setEnrollPhase('issuing');
    try {
      await apiClient
        .withIdempotencyKey(completeKeyRef.current ?? newIdempotencyKey())
        .post(
          `/api/v1/employees/${employeeId}/credentials/nfc-enrollments/${enrollment.enrollment_id}/complete`,
          label.trim() ? { label: label.trim() } : {},
          scopeOpts,
        );
      setEnrollPhase('success');
    } catch (err) {
      setEnrollError(
        err instanceof ApiError ? err.message : 'We couldn’t finish adding the fob. Please try again.',
      );
      setEnrollPhase('error');
    }
  }, [employeeId, enrollment, label, scopeOpts, tenantId]);

  // Poll the reader while the popup is "listening". On the first detected
  // card, auto-issue exactly once (guarded by completingRef).
  useEffect(() => {
    if (!tenantId || !employeeId || !enrollment || enrollPhase !== 'waiting') return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const poll = async () => {
      try {
        const res = await apiClient.get<{
          data: { token_present: boolean; card_id: string | null; status: 'waiting' | 'detected' };
        }>(
          `/api/v1/employees/${employeeId}/credentials/nfc-enrollments/${enrollment.enrollment_id}`,
          scopeOpts,
        );
        if (cancelled) return;
        if (res.data.token_present && !completingRef.current) {
          completingRef.current = true;
          void completeEnrollment();
          return;
        }
      } catch {
        // Transient poll failures are normal while the reader is idle —
        // keep listening rather than surfacing noise to the operator.
      }
      if (!cancelled) timer = setTimeout(poll, 2000);
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [completeEnrollment, employeeId, enrollment, enrollPhase, scopeOpts, tenantId]);

  // Live countdown for the scan window; flips to "timed out" at zero.
  useEffect(() => {
    if (enrollPhase !== 'waiting' || expiresAtRef.current == null) return undefined;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((expiresAtRef.current! - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        setEnrollPhase('expired');
        const session = enrollment;
        if (session && tenantId && employeeId) {
          void apiClient
            .delete(
              `/api/v1/employees/${employeeId}/credentials/nfc-enrollments/${session.enrollment_id}`,
              scopeOpts,
            )
            .catch(() => undefined);
        }
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [employeeId, enrollment, enrollPhase, scopeOpts, tenantId]);

  // Celebrate briefly, then tidy up so the page is ready for the next person.
  useEffect(() => {
    if (enrollPhase !== 'success') return undefined;
    const id = setTimeout(() => closeEnrollModal(), 2800);
    return () => clearTimeout(id);
  }, [closeEnrollModal, enrollPhase]);

  async function createPerson(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!tenantId) {
      setError('Choose a tenant first.');
      return;
    }
    if (!newName.trim() || !newEmail.trim()) {
      setError('Name and email are required.');
      return;
    }
    if (!newScheduleId) {
      setError('Schedule is required before creating a person.');
      return;
    }
    if (tenantAccessZones.length > 0 && !newAccessZoneId) {
      setError('Choose an access zone before creating a person.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await apiClient
        .withIdempotencyKey(createPersonKeyRef.current)
        .post<{ data: EmployeeRow }>(
          '/api/v1/employees',
          {
            name: newName.trim(),
            email: newEmail.trim().toLowerCase(),
            role: newRole,
            schedule_id: newScheduleId,
          },
          scopeOpts,
        );
      // The person now exists, so the next create is a new intent.
      createPersonKeyRef.current = newIdempotencyKey();
      setEmployees((current) => [
        res.data,
        ...current.filter((employee) => employee.id !== res.data.id),
      ]);
      setEmployeeId(res.data.id);
      setPersonMode('existing');
      const assignedZone = tenantAccessZones.find((zone) => zone.id === newAccessZoneId);
      if (newAccessZoneId) {
        try {
          await apiClient.post(
            `/api/v1/employees/${res.data.id}/access-zones/${newAccessZoneId}`,
            {},
            scopeOpts,
          );
        } catch (err) {
          setError(
            err instanceof ApiError
              ? `Person created, but the access zone was not assigned: ${err.message}`
              : 'Person created, but the access zone was not assigned.',
          );
          return;
        }
      }
      setZones((current) =>
        assignedZone
          ? [
              { ...assignedZone, assigned: true },
              ...current.filter((zone) => zone.id !== assignedZone.id),
            ]
          : current,
      );
      setToast(
        res.data.sync_status === 'synced' && res.data.unifi_user_id
          ? (assignedZone ? `Person ready. ${assignedZone.name} assigned.` : 'Person ready.')
          : 'Person saved. Controller sync is pending; credentials are not ready yet.',
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create person.');
    } finally {
      setBusy(false);
    }
  }

  async function startEnrollment() {
    if (!tenantId || !employeeId || !readerId) {
      setError('Choose a business, person, and reader first.');
      return;
    }
    setError(null);
    completingRef.current = false;
    completeKeyRef.current = newIdempotencyKey();
    expiresAtRef.current = null;
    setEnrollError(null);
    setEnrollVariant('scan');
    setSecondsLeft(null);
    setEnrollment(null);
    setEnrollPhase('waiting');
    try {
      const res = await apiClient
        // Starting a scan cancels the previous session, so each start is a
        // genuinely new intent rather than a retry of the last one. The
        // *completion* is what must de-duplicate, and that uses completeKeyRef.
        // eslint-disable-next-line no-restricted-syntax -- new scan session is a new intent; completion de-dupes via completeKeyRef
        .withIdempotencyKey(newIdempotencyKey())
        .post<{ data: EnrollmentSession }>(
          `/api/v1/employees/${employeeId}/credentials/nfc-enrollments`,
          {
            reader_id: readerId,
            ttl_seconds: 120,
            ...(label.trim() ? { label: label.trim() } : {}),
          },
          scopeOpts,
        );
      expiresAtRef.current = Date.now() + res.data.expires_in_sec * 1000;
      setSecondsLeft(res.data.expires_in_sec);
      setEnrollment(res.data);
    } catch (err) {
      setEnrollError(
        err instanceof ApiError ? err.message : 'We couldn’t start the reader. Please try again.',
      );
      setEnrollPhase('error');
    }
  }

  async function issueTouchPass() {
    if (!tenantId || !employeeId || !touchPassId) {
      setError('Choose a business, person, and Touch Pass first.');
      return;
    }
    if (enrollPhase === 'issuing') return;
    setError(null);
    setEnrollError(null);
    setEnrollVariant('instant');
    setEnrollPhase('issuing');
    try {
      await apiClient.withIdempotencyKey(issueCredentialKeyRef.current).post(
        `/api/v1/employees/${employeeId}/credentials`,
        {
          type: 'mobile_key',
          touch_pass_id: touchPassId,
          ...(label.trim() ? { label: label.trim() } : {}),
        },
        scopeOpts,
      );
      // Issued. The next one is a new intent; a retry of *this* one is not.
      issueCredentialKeyRef.current = newIdempotencyKey();
      setEnrollPhase('success');
    } catch (err) {
      setEnrollError(
        err instanceof ApiError ? err.message : 'We couldn’t add the Touch Pass. Please try again.',
      );
      setEnrollPhase('error');
    }
  }

  async function issuePinCode() {
    if (!tenantId || !employeeId) {
      setError('Choose a business and person first.');
      return;
    }
    if (!isValidPinCode(pinCode)) {
      setError('Enter a 4 to 8 digit PIN code.');
      return;
    }
    if (enrollPhase === 'issuing') return;
    setError(null);
    setEnrollError(null);
    setEnrollVariant('instant');
    setEnrollPhase('issuing');
    try {
      await apiClient.withIdempotencyKey(issueCredentialKeyRef.current).post(
        `/api/v1/employees/${employeeId}/credentials`,
        {
          type: 'pin_code',
          pin_code: pinCode.trim(),
          ...(label.trim() ? { label: label.trim() } : {}),
        },
        scopeOpts,
      );
      // Issued. The next one is a new intent; a retry of *this* one is not.
      issueCredentialKeyRef.current = newIdempotencyKey();
      setEnrollPhase('success');
    } catch (err) {
      setEnrollError(
        err instanceof ApiError ? err.message : 'We couldn’t create the PIN. Please try again.',
      );
      setEnrollPhase('error');
    }
  }

  // Shared "Try again" handler for the popup — restarts the right flow.
  function retryEnrollment() {
    if (enrollVariant === 'instant') {
      if (credentialKind === 'touch_pass') void issueTouchPass();
      else void issuePinCode();
      return;
    }
    setEnrollment(null);
    completingRef.current = false;
    void startEnrollment();
  }

  // Stop a scan that's still listening and free the reader.
  function cancelEnrollment() {
    const session = enrollment;
    closeEnrollModal();
    if (session && tenantId && employeeId) {
      void apiClient
        .delete(
          `/api/v1/employees/${employeeId}/credentials/nfc-enrollments/${session.enrollment_id}`,
          scopeOpts,
        )
        .catch(() => undefined);
    }
  }

  async function assignZone(zone: AccessZoneRow) {
    if (!tenantId || !employeeId) return;
    setBusy(true);
    setError(null);
    try {
      await apiClient.post(
        `/api/v1/employees/${employeeId}/access-zones/${zone.id}`,
        {},
        scopeOpts,
      );
      setZones((current) =>
        current.map((row) => (row.id === zone.id ? { ...row, assigned: true } : row)),
      );
      setToast(`${zone.name} assigned.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not assign Access Zone.');
    } finally {
      setBusy(false);
    }
  }

  // Why the scan button is disabled, in the order the operator would fix it.
  const scanBlockedReason: string | null =
    enrollPhase !== null
      ? 'A scan is already running.'
      : !tenantId
        ? 'Choose a business first.'
        : !employeeId
          ? 'Choose or create a person first — the fob is issued to them.'
          : !employeeReady
            ? 'The person must be active and synced with the controller before enrollment.'
            : !readerId
              ? 'Choose the reader the fob will be held against.'
              : null;

  // The blocking prerequisites, gathered rather than discovered one by one.
  const missingPrerequisites: Array<{ href: string; label: string }> = [
    schedules.length === 0 ? { href: '/schedules', label: 'a schedule' } : null,
    tenantAccessZones.length === 0 ? { href: '/access-groups', label: 'an Access Zone' } : null,
    !hasPinnedEnrollmentReader && readers.length === 0
      ? { href: '/hardware', label: 'a card reader' }
      : null,
  ].filter((m): m is { href: string; label: string } => m !== null);

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex items-center gap-3">
          <span className="rounded-lg bg-primary-container p-3 text-on-primary">
            <Icon name="badge" />
          </span>
          <div>
            <h1 className="font-headline-md text-headline-md text-on-surface">
              Credential Enrollment
            </h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Add or find a person, give them a key, and choose where they can go.
            </p>
          </div>
        </div>
      </header>

      {/* Enrollment has three prerequisites — a schedule, an Access Zone and a
          reader — and they used to be discovered one at a time, on scroll,
          after the operator had already started filling the form. Each one
          meant leaving the page, finding the right surface unaided, and coming
          back to re-enter everything.

          Stating them together, up front, with a link each, turns three failed
          attempts into one trip. Only shown when something is actually
          missing. */}
      {!loading && tenantId && missingPrerequisites.length > 0 ? (
        <div
          role="status"
          className="rounded-lg bg-tertiary-fixed px-4 py-3 text-on-tertiary-fixed"
        >
          <p className="font-label-md text-label-md uppercase">
            Not ready to enroll yet
          </p>
          <p className="mt-1 font-body-md text-body-md">
            This site still needs{' '}
            {missingPrerequisites.map((m, i) => (
              <span key={m.href}>
                {i > 0 ? (i === missingPrerequisites.length - 1 ? ' and ' : ', ') : ''}
                <Link to={m.href} className="underline hover:no-underline">
                  {m.label}
                </Link>
              </span>
            ))}
            . You can still add people now; they will not be able to open anything
            until this is done.
          </p>
        </div>
      ) : null}

      {toast ? (
        <div
          role="status"
          className="flex items-center gap-2 rounded-lg bg-secondary-container px-4 py-2 font-body-md text-body-md text-on-secondary-container"
        >
          <Icon name="check_circle" />
          <span className="flex-1">{toast}</span>
          <button type="button" className="underline" onClick={() => setToast(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      {error ? (
        <Card>
          <CardBody>
            <p role="alert" className="font-body-md text-body-md text-error">
              {error}
            </p>
          </CardBody>
        </Card>
      ) : null}

      <section className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)]">
        <div className="space-y-4">
          <Card>
            <CardBody>
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="font-title-lg text-title-lg text-on-surface">Enroll person</h2>
                {loading ? <Spinner /> : null}
              </div>

              <div className="grid gap-4">
                <Field label="Tenant" htmlFor="enroll-tenant">
                  <select
                    id="enroll-tenant"
                    value={tenantId}
                    onChange={(e) => {
                      setTenantId(e.target.value);
                      setEmployeeId('');
                      setNewScheduleId('');
                      setNewAccessZoneId('');
                      setEnrollment(null);
                    }}
                    disabled={!canPickTenant}
                    className="w-full rounded border border-outline-variant bg-surface px-3 py-2 text-on-surface"
                  >
                    <option value="">Select tenant</option>
                    {tenants.map((tenant) => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant={personMode === 'create' ? 'primary' : 'secondary'}
                    onClick={() => setPersonMode('create')}
                  >
                    Create new person
                  </Button>
                  <Button
                    type="button"
                    variant={personMode === 'existing' ? 'primary' : 'secondary'}
                    onClick={() => setPersonMode('existing')}
                    disabled={!tenantId}
                  >
                    Use existing person
                  </Button>
                </div>

                {personMode === 'create' ? (
                  <form
                    onSubmit={createPerson}
                    className="grid gap-3 rounded border border-outline-variant p-4"
                  >
                    <Field label="Name" htmlFor="enroll-name">
                      <input
                        id="enroll-name"
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        className="w-full rounded border border-outline-variant bg-surface px-3 py-2 text-on-surface"
                        maxLength={120}
                        required
                      />
                    </Field>
                    <Field label="Email" htmlFor="enroll-email">
                      <input
                        id="enroll-email"
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        className="w-full rounded border border-outline-variant bg-surface px-3 py-2 text-on-surface"
                        maxLength={254}
                        required
                      />
                    </Field>
                    <Field label="Role" htmlFor="enroll-role">
                      <select
                        id="enroll-role"
                        value={newRole}
                        onChange={(e) => setNewRole(e.target.value as EmployeeRole)}
                        className="w-full rounded border border-outline-variant bg-surface px-3 py-2 text-on-surface"
                      >
                        <option value="employee">Employee</option>
                        <option value="manager">Manager</option>
                      </select>
                    </Field>
                    <Field label="Schedule" htmlFor="enroll-schedule">
                      <select
                        id="enroll-schedule"
                        value={newScheduleId}
                        onChange={(e) => setNewScheduleId(e.target.value)}
                        required
                        className="w-full rounded border border-outline-variant bg-surface px-3 py-2 text-on-surface"
                      >
                        <option value="" disabled>
                          Select a schedule
                        </option>
                        {schedules.map((schedule) => (
                          <option key={schedule.id} value={schedule.id}>
                            {schedule.name}
                          </option>
                        ))}
                      </select>
                      {schedules.length === 0 ? (
                        <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
                          No schedules exist yet — a person needs one.{' '}
                          <Link to="/schedules" className="text-primary underline hover:no-underline">
                            Create a schedule
                          </Link>
                          , then come back here.
                        </p>
                      ) : null}
                    </Field>
                    <Field label="Access zone" htmlFor="enroll-access-zone">
                      <select
                        id="enroll-access-zone"
                        value={newAccessZoneId}
                        onChange={(e) => setNewAccessZoneId(e.target.value)}
                        required={tenantAccessZones.length > 0}
                        className="w-full rounded border border-outline-variant bg-surface px-3 py-2 text-on-surface"
                      >
                        <option value="" disabled>
                          Select an access zone
                        </option>
                        {tenantAccessZones.map((zone) => (
                          <option key={zone.id} value={zone.id}>
                            {zone.name}
                          </option>
                        ))}
                      </select>
                      {tenantAccessZones.length === 0 ? (
                        <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
                          No Access Zones exist yet — without one, a new person can be
                          created but will not be able to open anything.{' '}
                          <Link
                            to="/access-groups"
                            className="text-primary underline hover:no-underline"
                          >
                            Create an Access Zone
                          </Link>
                          .
                        </p>
                      ) : null}
                    </Field>
                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        disabled={
                          !tenantId ||
                          !newScheduleId ||
                          (tenantAccessZones.length > 0 && !newAccessZoneId) ||
                          busy
                        }
                        loading={busy}
                      >
                        Create person
                      </Button>
                    </div>
                  </form>
                ) : (
                  <Field label="Person" htmlFor="enroll-person">
                    <select
                      id="enroll-person"
                      value={employeeId}
                      onChange={(e) => setEmployeeId(e.target.value)}
                      className="w-full rounded border border-outline-variant bg-surface px-3 py-2 text-on-surface"
                      disabled={!tenantId}
                    >
                      <option value="">Select person</option>
                      {employees.map((employee) => (
                        <option key={employee.id} value={employee.id}>
                          {employee.name} ({employee.email})
                        </option>
                      ))}
                    </select>
                  </Field>
                )}

                {selectedEmployee ? (
                  <div className="rounded border border-outline-variant bg-surface-container-lowest p-3">
                    <div className="font-title-sm text-title-sm text-on-surface">
                      {selectedEmployee.name}
                    </div>
                    <div className="font-body-md text-body-md text-on-surface-variant">
                      {selectedEmployee.email}
                    </div>
                    <div className="mt-2 text-on-surface-variant">
                      {employeeReady ? 'Person ready.' : 'Person saved, but not ready for credentials. Check their status and controller sync.'}
                    </div>
                  </div>
                ) : null}

                <Field label="Credential label" htmlFor="enroll-label">
                  <input
                    id="enroll-label"
                    type="text"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    className="w-full rounded border border-outline-variant bg-surface px-3 py-2 text-on-surface"
                    maxLength={120}
                  />
                </Field>

                <div className="grid gap-2">
                  <span className="font-label-md text-label-md text-on-surface-variant">
                    Credential type
                  </span>
                  <div className="flex flex-wrap gap-2" role="group" aria-label="Credential type">
                    <Button
                      type="button"
                      variant={credentialKind === 'touch_pass' ? 'primary' : 'secondary'}
                      onClick={() => chooseCredentialKind('touch_pass')}
                      disabled={!touchPolicyEnabled}
                    >
                      Touch Pass
                    </Button>
                    <Button
                      type="button"
                      variant={credentialKind === 'nfc_fob' ? 'primary' : 'secondary'}
                      onClick={() => chooseCredentialKind('nfc_fob')}
                      disabled={!thirdPartyNfcEnabled}
                    >
                      Key fob or card
                    </Button>
                    <Button
                      type="button"
                      variant={credentialKind === 'pin_code' ? 'primary' : 'secondary'}
                      onClick={() => chooseCredentialKind('pin_code')}
                      disabled={!pinCodeEnabled}
                    >
                      PIN code
                    </Button>
                  </div>
                  {!touchPolicyEnabled || !thirdPartyNfcEnabled || !pinCodeEnabled ? (
                    <p className="font-body-sm text-body-sm text-on-surface-variant">
                      Some options are turned off for your site. An administrator can enable them
                      in settings.
                    </p>
                  ) : null}
                </div>

                {credentialKind === 'touch_pass' ? (
                  <div className="rounded border border-outline-variant p-4">
                    <Field label="Touch Pass" htmlFor="enroll-touch-pass">
                      <select
                        id="enroll-touch-pass"
                        value={touchPassId}
                        onChange={(e) => setTouchPassId(e.target.value)}
                        className="w-full rounded border border-outline-variant bg-surface px-3 py-2 text-on-surface"
                        disabled={!tenantId || busy}
                      >
                        <option value="">Select Touch Pass</option>
                        {touchPasses.map((row) => (
                          <option key={row.id} value={row.id}>
                            {(row.card_name && row.card_name.trim().length > 0
                              ? row.card_name
                              : row.card_id) ?? row.id}
                          </option>
                        ))}
                      </select>
                    </Field>
                    {touchPassLoadError ? (
                      <p className="mt-2 font-body-md text-body-md text-error" role="alert">
                        {touchPassLoadError}
                      </p>
                    ) : touchPasses.length === 0 ? (
                      <p className="mt-2 font-body-md text-body-md text-on-surface-variant">
                        No Touch Passes are available for this business yet. Add one and assign it
                        to this business first.
                      </p>
                    ) : null}
                    <div className="mt-3 flex justify-end">
                      <Button
                        type="button"
                        onClick={() => void issueTouchPass()}
                        disabled={
                          !tenantId || !employeeReady || !touchPolicyEnabled || !touchPassId || enrollPhase === 'issuing'
                        }
                      >
                        Add Touch Pass
                      </Button>
                    </div>
                  </div>
                ) : null}

                {credentialKind === 'nfc_fob' ? (
                  <>
                    <Field label="Reader" htmlFor="enroll-reader">
                      {hasPinnedEnrollmentReader ? (
                        <div className="rounded border border-outline-variant bg-surface-container-lowest px-3 py-2 text-on-surface">
                          <p className="font-body-md text-body-md">
                            {enrollmentReaderName ?? enrollmentReaderId}
                          </p>
                          <p className="font-body-sm text-body-sm text-on-surface-variant">
                            This reader is set by your site’s policy.
                          </p>
                          <input id="enroll-reader" type="hidden" value={readerId} readOnly />
                        </div>
                      ) : readers.length > 0 ? (
                        <select
                          id="enroll-reader"
                          value={readerId}
                          onChange={(e) => setReaderId(e.target.value)}
                          className="w-full rounded border border-outline-variant bg-surface px-3 py-2 text-on-surface"
                          disabled={!tenantId}
                        >
                          <option value="">Select reader</option>
                          {readers.map((reader) => (
                            <option key={reader.id} value={reader.id}>
                              {readerDisplayName(reader)} ({reader.status})
                            </option>
                          ))}
                        </select>
                      ) : (
                        <select
                          id="enroll-reader"
                          value=""
                          onChange={() => undefined}
                          className="w-full rounded border border-outline-variant bg-surface px-3 py-2 text-on-surface"
                          disabled
                        >
                          <option value="">No readers found</option>
                        </select>
                      )}
                    </Field>
                    {!hasPinnedEnrollmentReader && readers.length === 0 ? (
                      <p className="font-body-sm text-body-sm text-on-surface-variant">
                        No card readers were found on the controller. Readers appear here
                        once the controller reports them —{' '}
                        <Link to="/hardware" className="text-primary underline hover:no-underline">
                          check hardware
                        </Link>{' '}
                        to see what it is reporting.
                      </p>
                    ) : null}
                    <div className="flex flex-col gap-2">
                      <Button
                        type="button"
                        onClick={startEnrollment}
                        disabled={!tenantId || !employeeReady || !thirdPartyNfcEnabled || !readerId || enrollPhase !== null}
                        title={scanBlockedReason ?? undefined}
                      >
                        <Icon name="contactless" />
                        Start fob scan
                      </Button>
                      {/* A disabled control must not be a mystery. This button
                          was correctly disabled and said nothing about why —
                          the operator saw a greyed bar and had to infer the
                          cause from three separate places on the page. */}
                      <p className="font-body-sm text-body-sm text-on-surface-variant">
                        {scanBlockedReason ??
                          'A window opens and listens at the reader — just hold the fob or card against it and we’ll do the rest.'}
                      </p>
                    </div>
                  </>
                ) : null}

                {credentialKind === 'pin_code' ? (
                  <div className="rounded border border-outline-variant p-4">
                    <Field label="PIN code" htmlFor="enroll-pin-code">
                      <input
                        id="enroll-pin-code"
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        pattern="[0-9]*"
                        value={pinCode}
                        onChange={(e) => setPinCode(normalizePinCodeInput(e.target.value))}
                        maxLength={8}
                        className="w-full rounded border border-outline-variant bg-surface px-3 py-2 font-code text-code text-on-surface"
                      />
                    </Field>
                    <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant">
                      Use 4 to 8 digits. This is the code the person will type at a keypad.
                    </p>
                    <div className="mt-3 flex justify-end">
                      <Button
                        type="button"
                        onClick={() => void issuePinCode()}
                        disabled={!tenantId || !employeeReady || !pinCodeEnabled || enrollPhase === 'issuing' || !isValidPinCode(pinCode)}
                      >
                        Create PIN
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            </CardBody>
          </Card>
        </div>

        <Card>
          <CardBody>
            <h2 className="font-title-lg text-title-lg text-on-surface">Access Zones</h2>
            {!employeeId ? (
              <p className="mt-3 font-body-md text-body-md text-on-surface-variant">
                Pick or add a person first, then choose where they can go.
              </p>
            ) : zones.length === 0 ? (
              <p className="mt-3 font-body-md text-body-md text-on-surface-variant">
                No Access Zones are set up yet.
              </p>
            ) : (
              <div className="mt-3 space-y-2">
                {zones.map((zone) => (
                  <div
                    key={zone.id}
                    className="flex items-center justify-between gap-3 rounded border border-outline-variant p-3"
                  >
                    <div>
                      <div className="font-title-sm text-title-sm text-on-surface">{zone.name}</div>
                      <div className="font-body-sm text-body-sm text-on-surface-variant">
                        {zone.assigned ? 'Assigned' : 'Available'}
                      </div>
                    </div>
                    {zone.assigned ? (
                      <Badge tone="success">Assigned</Badge>
                    ) : (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => void assignZone(zone)}
                        disabled={busy}
                      >
                        Add {zone.name}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </section>

      <EnrollmentFeedback
        open={enrollPhase !== null}
        phase={enrollPhase ?? 'waiting'}
        variant={enrollVariant}
        readerName={
          enrollVariant === 'scan'
            ? (hasPinnedEnrollmentReader
                ? (enrollmentReaderName ?? enrollmentReaderId)
                : (readers.find((r) => r.id === readerId)
                    ? readerDisplayName(readers.find((r) => r.id === readerId)!)
                    : null))
            : null
        }
        personName={selectedEmployee?.name ?? null}
        credentialLabel={label}
        secondsLeft={secondsLeft}
        errorMessage={enrollError}
        onCancel={cancelEnrollment}
        onRetry={retryEnrollment}
        onClose={closeEnrollModal}
      />
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <label className="grid gap-1" htmlFor={htmlFor}>
      <span className="font-label-md text-label-md text-on-surface-variant">{label}</span>
      {children}
    </label>
  );
}
